import {clsx} from '@gfiber/typesmith';
import type React from 'react';
import 'material-icons/iconfont/material-icons.css';
import 'material-symbols/outlined.css';
import 'material-symbols/rounded.css';
import 'material-symbols/sharp.css';
import styles from './Icon.module.scss';
import type {IconProps} from './Icon.types';

const _OUTLINED = /_outlined$/;
const _ROUNDED = /_rounded$/;
const _SHARP = /_sharp$/;

/**
 * Strips variant suffixes from icon ligature names
 */
function stripVariantSuffix(ligature: string): string {
  return ligature
    .replace(_OUTLINED, '')
    .replace(_ROUNDED, '')
    .replace(_SHARP, '');
}

/**
 * Maps fontSize prop to CSS class
 */
function getFontSizeClass(
  fontSize?: IconProps['fontSize'],
): string | undefined {
  switch (fontSize) {
    case 'xsmall':
      return styles.xsmall;
    case 'small':
      return styles.small;
    case 'medium':
      return styles.medium;
    case 'large':
      return styles.large;
    case 'xlarge':
      return styles.xlarge;
    case 'inherit':
      return styles.inherit;
    default:
      return undefined;
  }
}

/**
 * Creates a Material Icon component factory
 *
 * @param ligature - The Material Icon ligature name
 * @param initialProps - Optional initial props to merge
 * @returns A function that creates an icon component
 *
 * @example
 * ```tsx
 * export const AddIcon = createIcon('add');
 * <AddIcon fontSize="large" />
 * ```
 */
export const createIcon =
  (ligature: string, initialProps?: IconProps) =>
  (templateProps: IconProps = {}): React.JSX.Element => {
    const {className, fontSize, ...restProps} = {
      ...initialProps,
      ...templateProps,
    };

    const iconText = stripVariantSuffix(ligature);

    return (
      <span
        {...restProps}
        className={clsx(
          'material-icons',
          _OUTLINED.test(ligature) && 'material-icons-outlined',
          _ROUNDED.test(ligature) && 'material-icons-rounded',
          _SHARP.test(ligature) && 'material-icons-sharp',
          getFontSizeClass(fontSize),
          className,
        )}
      >
        {iconText}
      </span>
    );
  };

/**
 * Creates a Material Symbol component factory
 *
 * @param ligature - The Material Symbol ligature name
 * @param initialProps - Optional initial props to merge
 * @returns A function that creates an icon component
 *
 * @example
 * ```tsx
 * export const PasskeySymbolIcon = createSymbolIcon('passkey');
 * <PasskeySymbolIcon fontSize="large" />
 * ```
 */
export const createSymbolIcon =
  (ligature: string, initialProps?: IconProps) =>
  (templateProps: IconProps = {}): React.JSX.Element => {
    const {className, fontSize, ...restProps} = {
      ...initialProps,
      ...templateProps,
    };

    let basename = 'material-symbols-outlined';

    if (_ROUNDED.test(ligature)) {
      basename = 'material-symbols-rounded';
    } else if (_SHARP.test(ligature)) {
      basename = 'material-symbols-sharp';
    }

    const iconText = stripVariantSuffix(ligature);

    return (
      <span
        {...restProps}
        className={clsx(basename, getFontSizeClass(fontSize), className)}
      >
        {iconText}
      </span>
    );
  };
